import { safeAPI } from "./appUtils";
import { comparableIdentifierValue } from "./carrierIdentity";

/**
 * Ask FMCSA's register who holds a number, as a carrier's paperwork is typed.
 *
 * The answer is what makes the difference between a company record built out of
 * whatever four yards typed and one built on the carrier's registered legal name.
 * A number that does not exist is caught at the point somebody can still ask the
 * driver, rather than after it has quietly placed the carrier in a company of its
 * own — a wrong number is the main way one carrier becomes two companies.
 *
 * Goes through the carrierLookup Lambda rather than the register directly. The
 * service sends no CORS headers, so a browser cannot reach it, and the webKey is
 * rate-limited per account and has no business in a bundle.
 *
 * Nothing here ever throws or blocks a save. A register that is down, a key that
 * is misconfigured and a Canadian carrier FMCSA has never heard of are all states
 * the form renders and carries on from — the number is saved either way.
 */

// The schemes FMCSA issues, and so the only ones worth a round trip. Kept in step
// with SUPPORTED_SCHEMES in the Lambda's lib/fmcsa.js by a drift test, for the
// same reason the identity reductions are: a scheme listed on one side and not
// the other either wastes a call or silently stops checking a number.
export const FMCSA_SCHEMES = ["USDOT", "MC", "MX"];

export const isFmcsaScheme = (scheme) =>
  FMCSA_SCHEMES.includes(
    String(scheme ?? "")
      .trim()
      .toUpperCase(),
  );

/**
 * The cache key: the scheme and the number as it compares, so the same number
 * typed with a prefix, a dash or a leading zero is one lookup rather than four.
 */
const cacheKey = (scheme, value) => {
  const normalizedScheme = String(scheme ?? "")
    .trim()
    .toUpperCase();
  const comparable = comparableIdentifierValue(normalizedScheme, value);
  return comparable ? `${normalizedScheme}#${comparable}` : null;
};

/**
 * Answers already given, for the life of the page.
 *
 * A form re-verifies as the user tabs between fields, corrects a typo back to
 * what it was, or reopens the same carrier — and the register's answer for one
 * number does not change over a few minutes of typing. Failures are cached too,
 * but briefly: a key that was fixed or a service that came back should be picked
 * up without a reload.
 */
const answers = new Map();
const FAILURE_TTL_MS = 30000;

const cached = (key) => {
  const hit = answers.get(key);
  if (!hit) return null;
  if (hit.expiresAt && hit.expiresAt < Date.now()) {
    answers.delete(key);
    return null;
  }
  return hit.result;
};

const remember = (key, result) =>
  answers.set(key, {
    result,
    expiresAt: result?.unavailable ? Date.now() + FAILURE_TTL_MS : null,
  });

/** Forget everything, so a test starts from nothing. */
export const clearCarrierLookupCache = () => answers.clear();

/**
 * @param {{scheme: string, value: string}} identifier
 * @returns {Promise<object|null>} null when there is nothing worth asking about —
 *   no number yet, or a scheme this register does not answer for. Otherwise the
 *   Lambda's verdict: `{found}`, `{found, carrier}`, `{found, ambiguous,
 *   candidates}` or `{unavailable, reason}`.
 */
export const lookupCarrierIdentifier = async ({ scheme, value }) => {
  if (!isFmcsaScheme(scheme)) return null;

  const key = cacheKey(scheme, value);
  if (!key) return null;

  const hit = cached(key);
  if (hit) return hit;

  try {
    const result = await safeAPI.post("transREST", "/carrierLookup/verify", {
      body: { scheme, value },
    });
    remember(key, result);
    return result;
  } catch (error) {
    // Not reported through logError: a lookup failing is a normal outcome of a
    // form being filled in on a bad connection, and it is already shown in the
    // field. Logging it as an application error would bury the ones that matter.
    console.warn("carrierLookup:", error?.message || error);
    const result = { scheme, value, unavailable: true, reason: "error" };
    remember(key, result);
    return result;
  }
};

/**
 * The register's verdict as one line, plus how loudly to say it.
 *
 * Severity, not just text, because these are three different things to a yard: a
 * name to accept, a number to go back and ask about, and a carrier that may not
 * legally take the load.
 *
 * @returns {{severity: "success"|"info"|"warning"|"error", text: string,
 *   legalName: string|null}|null}
 */
export const describeLookup = (result) => {
  if (!result) return null;
  if (result.supported === false) return null;

  if (result.unavailable)
    return {
      severity: "info",
      text:
        result.reason === "timeout"
          ? "FMCSA did not answer in time — the number was not checked."
          : "Could not reach FMCSA — the number was not checked.",
      legalName: null,
    };

  if (!result.found)
    return {
      severity: "warning",
      text: "FMCSA has no carrier registered under this number.",
      legalName: null,
    };

  if (result.ambiguous)
    return {
      severity: "warning",
      text: `More than one carrier holds this docket number (${(
        result.candidates || []
      )
        .map((row) => row.legalName)
        .filter(Boolean)
        .join(", ")}). Check which one this is.`,
      legalName: null,
    };

  const carrier = result.carrier || {};
  const name = carrier.legalName || carrier.dbaName || null;

  // Said before the name, because it is the part that decides whether the load
  // can be given to them at all. A carrier can be registered and still barred.
  if (carrier.outOfService)
    return {
      severity: "error",
      text: `${name || "This carrier"} is out of service${
        carrier.outOfServiceDate ? ` as of ${carrier.outOfServiceDate}` : ""
      } according to FMCSA.`,
      legalName: name,
    };

  // Only an explicit "N" from the register is a refusal. An absent verdict
  // (null) is handled below as "could not confirm" — flagging it red here would
  // tell a yard its carrier is barred over a record the register said nothing
  // about, which is the wrong number to make somebody re-type.
  if (carrier.allowedToOperate === false)
    return {
      severity: "error",
      text: `FMCSA does not list ${
        name || "this carrier"
      } as allowed to operate.`,
      legalName: name,
    };

  // A registration the register no longer calls active: the carrier has stopped
  // filing its MCS-150, or has told FMCSA it no longer operates interstate. Not a
  // refusal — plenty of working carriers sit here between biennial filings, and
  // the register is quite happy to say a carrier may operate while calling its
  // registration inactive. So the name is still worth taking; it is the currency
  // of the record that is in doubt, and that is worth a yard's second look before
  // a load rather than after an audit.
  if (carrier.statusCode && carrier.statusCode !== "A")
    return {
      severity: "warning",
      text: `FMCSA: ${name} — registration is not active (status ${carrier.statusCode}). Worth confirming with the carrier.`,
      legalName: name,
    };

  // The register held a record but no operating-authority verdict on it —
  // neither the "N" that bars above nor a "Y". Said as a caveat, not a clean
  // success it did not earn: the yard is told the check came back blank on this
  // one point so it can ask, rather than shown a green line that overstates
  // what the register actually confirmed.
  if (carrier.allowedToOperate == null)
    return {
      severity: "warning",
      text: `FMCSA lists ${
        name || "this carrier"
      } but does not say whether they are allowed to operate. Worth confirming with the carrier.`,
      legalName: name,
    };

  return {
    severity: "success",
    text: `FMCSA: ${name}${
      carrier.dbaName && carrier.dbaName !== name
        ? ` (trading as ${carrier.dbaName})`
        : ""
    }`,
    legalName: name,
  };
};
